import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { listStaff } from "../../../lib/staff";
import { useAuth } from "../../../auth/AuthProvider";
import { listProducts, getServiceConsumables } from "../../../lib/inventory";

const ServiceForm = ({
  onSubmit,
  onCancel,
  editingService = null,
  existingCategories = [],
}) => {
  const auth = useAuth();
  const canSeeInventory = Boolean(auth?.permissions?.canSeeInventory);

  const [formData, setFormData] = useState({
    name: editingService?.name || "",
    description: editingService?.description || "",
    category: editingService?.category || "",
    price:
      editingService?.price !== undefined && editingService?.price !== null
        ? Number(editingService.price)
        : "",
    durationMinutes: editingService?.duration_minutes || 60,
    isActive: editingService?.active ?? true,
    // staff selectat la nivel de UI (id-uri de user)
    assignedStaff: Array.isArray(editingService?.assignedStaff)
      ? editingService.assignedStaff.map((s) => s.id)
      : [],
    advanceBookingDays: editingService?.advanceBookingDays || 30,
    allowOnlineBooking: editingService?.allowOnlineBooking ?? true,
    // Payment settings
    requiresPayment: editingService?.requires_payment ?? false,
    depositPercentOverride:
      editingService?.deposit_percent_override !== undefined &&
      editingService?.deposit_percent_override !== null
        ? Number(editingService.deposit_percent_override)
        : "",
  });

  const [errors, setErrors] = useState({});
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [consumables, setConsumables] = useState([]); // { product_id, qty_default }
  const [loadingConsumables, setLoadingConsumables] = useState(false);

  // încărcăm staff-ul pentru business-ul curent
  useEffect(() => {
    let alive = true;
    setLoadingStaff(true);

    listStaff()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];
        setStaff(arr);

        // dacă edităm un serviciu, marcăm staff-ul care are deja serviciul în profil
        if (editingService?.id) {
          const selectedIds = arr
            .filter(
              (member) =>
                Array.isArray(member.services) &&
                member.services.includes(editingService.id)
            )
            .map((m) => m.id);
          if (selectedIds.length) {
            setFormData((prev) => ({
              ...prev,
              assignedStaff: selectedIds,
            }));
          }
        }
      })
      .catch(() => {
        if (!alive) return;
        setStaff([]);
      })
      .finally(() => {
        if (alive) setLoadingStaff(false);
      });

    return () => {
      alive = false;
    };
  }, [editingService?.id]);


  // produse (inventory) pentru select + consumabile existente (la edit)
  useEffect(() => {
    if (!canSeeInventory) return;
    let alive = true;

    setLoadingProducts(true);
    listProducts()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];
        setProducts(arr);
      })
      .catch(() => {
        if (!alive) return;
        setProducts([]);
      })
      .finally(() => {
        if (alive) setLoadingProducts(false);
      });

    return () => {
      alive = false;
    };
  }, [canSeeInventory]);

  useEffect(() => {
    if (!canSeeInventory) return;

    // la create nu avem serviceId încă → consumabilele se salvează după create
    if (!editingService?.id) {
      setConsumables([]);
      return;
    }

    let alive = true;
    setLoadingConsumables(true);

    getServiceConsumables(editingService.id)
      .then((items) => {
        if (!alive) return;
        const arr = Array.isArray(items) ? items : [];
        const norm = arr
          .map((it) => ({
            product_id: String(it.product_id ?? it.product?.id ?? ""),
            qty_default:
              it.qty_default ??
              it.quantity ??
              it.qty ??
              "",
          }))
          .filter((x) => x.product_id);

        setConsumables(norm);
      })
      .catch(() => {
        if (!alive) return;
        setConsumables([]);
      })
      .finally(() => {
        if (alive) setLoadingConsumables(false);
      });

    return () => {
      alive = false;
    };
  }, [canSeeInventory, editingService?.id]);

  const durationOptions = [
    { value: 30, label: "30 minute" },
    { value: 45, label: "45 minute" },
    { value: 60, label: "1 oră" },
    { value: 90, label: "1 oră 30 minute" },
    { value: 120, label: "2 ore" },
    { value: 150, label: "2 ore 30 minute" },
    { value: 180, label: "3 ore" },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = "Numele serviciului este obligatoriu";
    }

    // descrierea este opțională – nu mai validăm

    if (!formData?.category) {
      newErrors.category = "Categoria este obligatorie";
    }

    if (!formData?.price || Number(formData?.price) <= 0) {
      newErrors.price = "Prețul trebuie să fie mai mare decât 0";
    }

    if (!formData?.durationMinutes || Number(formData?.durationMinutes) <= 0) {
      newErrors.durationMinutes = "Durata trebuie să fie mai mare decât 0";
    }

    // Personal alocat este opțional – nu îl facem obligatoriu

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleToggleStaff = (staffId) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.assignedStaff)
        ? prev.assignedStaff
        : [];
      const exists = current.includes(staffId);
      return {
        ...prev,
        assignedStaff: exists
          ? current.filter((id) => id !== staffId)
          : [...current, staffId],
      };
    });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      description: (formData.description || "").trim(),
      category: formData.category, // ales din dropdown
      price: Number(formData.price),
      duration_minutes: Number(formData.durationMinutes),
      active: !!formData.isActive,
      // Payment settings
      requires_payment: !!formData.requiresPayment,
      deposit_percent_override:
        formData.depositPercentOverride !== "" && formData.depositPercentOverride !== null
          ? Number(formData.depositPercentOverride)
          : null,
      // NU trimitem assignedStaff la ServiceSerializer (ar da eroare);
      // legarea staff–serviciu se face separat, în ServiceConfiguration.
    };

    const consumablesItems =
      canSeeInventory && Array.isArray(consumables)
        ? consumables
            .map((r) => ({
              product_id: r?.product_id ? Number(r.product_id) : null,
              qty_default: Number(r?.qty_default),
            }))
            .filter(
              (r) =>
                r.product_id &&
                Number.isFinite(r.qty_default) &&
                r.qty_default > 0
            )
            .map((r) => ({
              product_id: r.product_id,
              qty_default: Math.round(r.qty_default * 10) / 10,
            }))
        : [];

    onSubmit(payload, {
      assignedStaffIds: Array.isArray(formData.assignedStaff)
        ? formData.assignedStaff
        : [],
      consumables: consumablesItems,
    });
  };

  const categoryOptions =
    existingCategories && existingCategories.length
      ? existingCategories.map((c) => ({ value: c, label: c }))
      : [];

  const categoryDescription =
    existingCategories.length === 0
      ? "Nu există încă nicio categorie creată. Adaugă o categorie mai sus."
      : undefined;

  const hasStaff = staff.length > 0;

  const displayStaffName = (member) => {
    const first = (member.first_name || "").trim();
    const last = (member.last_name || "").trim();
    const full = `${first} ${last}`.trim();
    return full || member.email || `Utilizator #${member.id}`;
  };

  const round1Str = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return "";
    return String(Math.round(n * 10) / 10);
  };

  const handleAddConsumableRow = () => {
    setConsumables((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      { product_id: "", qty_default: "" },
    ]);
  };

  const handleRemoveConsumableRow = (idx) => {
    setConsumables((prev) =>
      (Array.isArray(prev) ? prev : []).filter((_, i) => i !== idx)
    );
  };

  const handleUpdateConsumable = (idx, patch) => {
    setConsumables((prev) =>
      (Array.isArray(prev) ? prev : []).map((row, i) =>
        i === idx ? { ...row, ...patch } : row
      )
    );
  };

  const productOptions = (Array.isArray(products) ? products : []).map((p) => ({
    value: String(p.id),
    label: `${p?.name || `Produs #${p.id}`}${
      p?.sku ? ` (${p.sku})` : ""
    }`,
  }));


  return (
    <div className="bg-background border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {editingService ? "Editează Serviciul" : "Adaugă Serviciu Nou"}
        </h2>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <Icon name="X" size={20} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Numele Serviciului"
              type="text"
              placeholder="ex. Tuns și Coafat"
              value={formData?.name}
              onChange={(e) => handleInputChange("name", e?.target?.value)}
              error={errors?.name}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Descrierea Serviciului <span className="text-xs">(optional)</span>
              </label>
              <textarea
                placeholder="Descrieți serviciul oferit (opțional)…"
                value={formData?.description}
                onChange={(e) =>
                  handleInputChange("description", e?.target?.value)
                }
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
              {errors?.description && (
                <p className="text-sm text-error mt-1">
                  {errors?.description}
                </p>
              )}
            </div>

            {/* CATEGORIE – dropdown, obligatoriu */}
            <Select
              label="Categoria"
              placeholder="Selectează categoria"
              options={categoryOptions}
              value={formData.category}
              onChange={(value) => handleInputChange("category", value)}
              error={errors?.category}
              required
              description={categoryDescription}
            />
          </div>

          <div className="space-y-4">
            <Input
              label="Preț (RON)"
              type="number"
              placeholder="0.00"
              value={formData?.price}
              onChange={(e) =>
                handleInputChange(
                  "price",
                  e?.target?.value === ""
                    ? ""
                    : parseFloat(e?.target?.value) || ""
                )
              }
              error={errors?.price}
              required
            />

            <Select
              label="Durata"
              placeholder="Selectează durata"
              options={durationOptions}
              value={formData?.durationMinutes}
              onChange={(value) =>
                handleInputChange("durationMinutes", value)
              }
              error={errors?.durationMinutes}
              required
            />

            <Input
              label="Zile Avans Programare"
              type="number"
              placeholder="30"
              value={formData?.advanceBookingDays}
              onChange={(e) =>
                handleInputChange(
                  "advanceBookingDays",
                  e?.target?.value === ""
                    ? 0
                    : parseInt(e?.target?.value, 10) || 0
                )
              }
              description="Câte zile în avans pot fi făcute programări"
            />
          </div>
        </div>

        {/* SETĂRI PLĂȚI */}
        <div className="p-4 bg-muted rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-foreground">
                Setări Plăți
              </h3>
              <p className="text-xs text-text-secondary mt-1">
                Configurează dacă acest serviciu necesită plată avans de la clienți
              </p>
            </div>
          </div>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              className="form-checkbox rounded border-border"
              checked={formData.requiresPayment}
              onChange={(e) =>
                handleInputChange("requiresPayment", e.target.checked)
              }
            />
            <span className="text-sm text-foreground">
              Necesită plată avans (prin Stripe Connect)
            </span>
          </label>

          {formData.requiresPayment && (
            <div className="ml-7 mt-3 space-y-2">
              <Input
                label="Procent avans custom (%)"
                type="number"
                placeholder="Ex: 30 (lăsați gol pentru default business)"
                value={formData.depositPercentOverride}
                onChange={(e) =>
                  handleInputChange(
                    "depositPercentOverride",
                    e?.target?.value === ""
                      ? ""
                      : parseFloat(e?.target?.value) || ""
                  )
                }
                description="Dacă lăsați gol, se va folosi procentul default al business-ului (30%)"
              />
              <p className="text-xs text-text-secondary">
                💡 Clienții vor fi redirecționați la Stripe pentru plată după selectarea programării.
              </p>
            </div>
          )}
        </div>

        {/* PERSONAL ALOCAT */}
        <div className="p-4 bg-muted rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-foreground">
            Personal alocat
          </h3>
          <p className="text-xs text-text-secondary">
            Selectează membrii de personal care pot presta acest serviciu
            (opțional).
          </p>

          {loadingStaff ? (
            <p className="text-sm text-text-secondary">Se încarcă personalul…</p>
          ) : !hasStaff ? (
            <p className="text-sm text-text-secondary italic">
              Nu există încă personal înregistrat pentru acest business.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {staff.map((member) => {
                const checked = Array.isArray(formData.assignedStaff)
                  ? formData.assignedStaff.includes(member.id)
                  : false;
                return (
                  <label
                    key={member.id}
                    className="flex items-center space-x-2 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/70"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox rounded border-border"
                      checked={checked}
                      onChange={() => handleToggleStaff(member.id)}
                    />
                    <span className="text-sm text-foreground">
                      {displayStaffName(member)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* CONSUMABILE (INVENTORY) */}
        {canSeeInventory && (
          <div className="p-4 bg-muted rounded-lg space-y-3">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-foreground">
                Consumabile (stocuri)
              </h3>
              <p className="text-xs text-text-secondary">
                Setează ce produse se consumă implicit pentru acest serviciu.
                Se scad automat când o programare este marcată „completed".
              </p>
            </div>

            {loadingProducts || loadingConsumables ? (
              <p className="text-sm text-text-secondary">
                Se încarcă produsele…
              </p>
            ) : productOptions.length === 0 ? (
              <p className="text-sm text-text-secondary italic">
                Nu ai produse încă. Creează produse în „Stocuri" și revino aici.
              </p>
            ) : (
              <div className="space-y-2">
                {Array.isArray(consumables) && consumables.length === 0 ? (
                  <p className="text-xs text-text-secondary italic">
                    (Opțional) Nu ai setat consumabile.
                  </p>
                ) : null}

                {(Array.isArray(consumables) ? consumables : []).map(
                  (row, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end"
                    >
                      <div className="md:col-span-7">
                        <Select
                          placeholder="Produs"
                          options={productOptions}
                          value={row?.product_id || ""}
                          onChange={(value) =>
                            handleUpdateConsumable(idx, { product_id: value })
                          }
                          searchable
                          clearable
                        />
                      </div>

                      <div className="md:col-span-4">
                        <Input
                          placeholder="Cantitate (max 1 zec.)"
                          type="number"
                          step="0.1"
                          value={row?.qty_default ?? ""}
                          onChange={(e) =>
                            handleUpdateConsumable(idx, {
                              qty_default: e?.target?.value,
                            })
                          }
                          onBlur={(e) =>
                            handleUpdateConsumable(idx, {
                              qty_default: round1Str(e?.target?.value),
                            })
                          }
                        />
                      </div>

                      <div className="md:col-span-1 flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveConsumableRow(idx)}
                          title="Șterge"
                        >
                          <Icon name="Trash2" size={18} />
                        </Button>
                      </div>
                    </div>
                  )
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="mt-2 inline-flex items-center"
                  onClick={handleAddConsumableRow}
                  disabled={productOptions.length === 0}
                >
                  <Icon name="PlusCircle" size={18} className="mr-2" />
                  Adaugă consumabil
                </Button>
              </div>
            )}
          </div>
        )}

        {/* SETĂRI DISPONIBILITATE */}
        <div className="flex flex-col space-y-4 p-4 bg-muted rounded-lg">
          <h3 className="text-sm font-medium text-foreground">
            Setări Disponibilitate
          </h3>

          <Checkbox
            label="Serviciu Activ"
            description="Serviciul este disponibil pentru programări"
            checked={formData?.isActive}
            onChange={(e) =>
              handleInputChange("isActive", e?.target?.checked)
            }
          />

          <Checkbox
            label="Permite Programări Online"
            description="Clienții pot programa acest serviciu online"
            checked={formData?.allowOnlineBooking}
            onChange={(e) =>
              handleInputChange("allowOnlineBooking", e?.target?.checked)
            }
          />
        </div>

        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
          <Button variant="outline" onClick={onCancel}>
            Anulează
          </Button>
          <Button type="submit" variant="default">
            {editingService ? "Actualizează Serviciul" : "Adaugă Serviciul"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;
